import { useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';
import { LatestPositionsResponse, LatestStockQuote } from '../types/latestPrice';

/** The provider's minute bars refresh once a minute; never poll faster. */
export const LATEST_POLL_MS = 60_000;

/**
 * Run `fn` now and every `LATEST_POLL_MS`, but only while the tab is visible
 * (a hidden tab stops polling; showing it again fetches at once).
 */
function useVisiblePolling(key: string, fn: () => Promise<void>) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!key) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const tick = () => { fnRef.current().catch(() => undefined); };
    const start = () => {
      if (timer || document.visibilityState !== 'visible') return;
      tick();
      timer = setInterval(tick, LATEST_POLL_MS);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => (document.visibilityState === 'visible' ? start() : stop());
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [key]);
}

/**
 * Latest delayed price per symbol (GET /api/stocks/latest-prices). Display
 * only: it never replaces the 15-min Price and never feeds a calculation.
 */
export function useLatestPrices(symbols: string[]): Map<string, LatestStockQuote> {
  const [quotes, setQuotes] = useState<Map<string, LatestStockQuote>>(new Map());
  const key = symbols.length ? Array.from(new Set(symbols)).sort().join(',') : '';
  useEffect(() => { setQuotes(new Map()); }, [key]);
  useVisiblePolling(key, async () => {
    if (!localStorage.getItem('token')) return;
    const res = await apiService.getLatestPrices(key.split(','));
    setQuotes(new Map(Object.entries(res.quotes || {})));
  });
  return quotes;
}

/**
 * Latest P&L of the user's open positions (GET /api/portfolio/positions/latest),
 * computed by the server with the same definitions as the 15-min P&L.
 * `enabled` = false (no positions) skips the request.
 */
export function useLatestPositionPnl(enabled: boolean, refreshKey = ''): LatestPositionsResponse | null {
  const [data, setData] = useState<LatestPositionsResponse | null>(null);
  const key = enabled ? `positions:${refreshKey}` : '';
  useEffect(() => { if (!enabled) setData(null); }, [enabled]);
  useVisiblePolling(key, async () => {
    if (!localStorage.getItem('token')) return;
    setData(await apiService.getLatestPositionPnl());
  });
  return data;
}
